import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Target, Users, Calendar, FileText, ShieldCheck, GraduationCap, Cpu,
  MessageSquare, BookOpen, UserCheck, Layers
} from 'lucide-react';
import PreviewBanner from './PreviewBanner';

const disciplines = [
  { icon: Target,         title: 'Project delivery excellence',                                            desc: 'Helping teams plan, manage, and deliver projects with clarity and consistency.' },
  { icon: Users,          title: 'Stakeholder management',                                                 desc: 'Strengthening communication, alignment, and engagement across internal and external stakeholders.' },
  { icon: Calendar,       title: 'Lifecycle & asset management',                                           desc: 'Ensuring assets are planned, maintained, and managed effectively throughout their lifecycle.' },
  { icon: FileText,       title: 'Lifecycle, capital & minor works delivery frameworks',                   desc: 'Providing structured frameworks that support the smooth delivery of capital programmes and smaller works.' },
  { icon: ShieldCheck,    title: 'CDM & health & safety compliance frameworks',                            desc: 'Supporting your existing compliance requirements and embedding them directly into the project delivery process, so safe systems of work are consistently applied and easy for teams to follow.' },
  { icon: GraduationCap,  title: 'Leadership, team development & cultural alignment',                      desc: 'Building capability, confidence, and a shared way of working across teams.' },
  { icon: Cpu,            title: 'Digital transformation & workflow mapping through the Videra platform', desc: 'Using our proprietary Videra ecosystem to simplify processes, improve visibility, and create auditable, repeatable systems.' },
];

const engagements = [
  { icon: MessageSquare, title: 'Advisory',  desc: 'Expert guidance and governance support — strategic input where and when you need it.' },
  { icon: BookOpen,      title: 'Workshops', desc: 'Targeted sessions to diagnose issues and build capability inside your team.' },
  { icon: UserCheck,     title: 'Embedded',  desc: 'Hands-on delivery support working inside your teams day-to-day.' },
  { icon: Layers,        title: 'Programme', desc: 'Full transformation programmes powered by Videra — process, people and platform delivered together.' },
];

const PreviewConsultancy = () => (
  <div data-testid="preview-consultancy">
    <PreviewBanner label="Consultancy" livePath="/consultancy" liveLabel="Consultancy" />

    {/* HERO */}
    <section className="pt-16 pb-12 md:pt-24 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Services</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
          What we deliver.
        </h1>
        <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
          Keystone supports organisations across seven practical, delivery-focused disciplines —
          combining operational expertise, training, and the Videra platform to make
          transformation stick.
        </p>
      </div>
    </section>

    {/* WHAT WE DELIVER — 7 DISCIPLINES */}
    <section className="py-20 md:py-24 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {disciplines.map((d, i) => (
            <div key={d.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-7 hover:border-slate-900 transition-colors flex gap-5">
              <div className="flex flex-col items-center gap-3 pt-1">
                <span className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center">
                  <d.icon className="w-5 h-5" />
                </span>
                <span className="text-xs text-slate-400 font-semibold tracking-tight">{String(i+1).padStart(2,'0')}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-medium text-slate-900 leading-snug">{d.title}</h3>
                <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* HOW WE WORK TOGETHER — 4 ENGAGEMENT MODELS */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">How we work together</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            We tailor our engagement model to your needs.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            From light-touch advisory through to full transformation programmes — pick the
            commercial model that fits where you are today.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {engagements.map((e) => (
            <div key={e.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-slate-900 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center mb-4">
                <e.icon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-medium text-slate-900">{e.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{e.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 bg-slate-950 text-white text-center">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">Ready to transform your delivery?</h2>
        <p className="mt-6 text-lg text-slate-400">Let&apos;s talk about how Keystone and Videra can elevate your operational performance.</p>
        <Link to="/contact" className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium rounded-md hover:bg-slate-100 transition-all mt-10 group">
          Speak to our team <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  </div>
);

export default PreviewConsultancy;
